import {
  Create,
  Edit,
  ImageField,
  ImageInput,
  LoadingIndicator,
  required,
  Resource,
  TextInput,
  Toolbar,
  ToolbarProps,
  useCreateContext,
  useInput,
  useRecordContext,
  useRedirect,
} from "react-admin";
import { Box, SxProps } from "@mui/material";
import { Column } from "../misc/Column";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { DefaultList } from "../misc/DefaultList";
import { DrivingSchoolCustomCard } from "../providers/drivingSchoolCustomCardsProvider";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { SaveButton2 } from "../buttons/SaveButton2";
import { grants } from "../backoffice.access_control";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Form2 } from "../misc/Form2";
import { url } from "../utils/validators";
import { AccessDenied } from "../misc/AccessDenied";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

const _fileInputDropZoneStyle: SxProps = {
  "& .RaFileInput-dropZone": {
    border: 1,
    borderColor: "divider",
    borderStyle: "dashed",
    borderRadius: "8px",
    backgroundColor: "background.default",
    padding: "20px 16px",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
  },
  "& .RaFileInput-dropZone:hover": {
    borderColor: "text.secondary",
    backgroundColor: "action.hover",
  },
};

function CreateCustomCardForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();

  if (!grants.has("CustomCard:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create
      resource="customCards"
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Teaser erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/customCards`);
        },
        onError: (error: Error) => {
          showErrorToast("Fehler beim Erstellen des Teasers.");
          console.error("Failed to create drivingSchoolCustomCard.", error);
        },
      }}
      transform={(data: Omit<DrivingSchoolCustomCard, "drivingSchoolId">): Partial<DrivingSchoolCustomCard> => ({
        ...data,
        drivingSchoolId,
      })}
    >
      <DrivingSchoolCustomCardForm mode="create" />
    </Create>
  );
}

function EditCustomCardForm() {
  const redirect = useRedirect();
  const customCard = useRecordAndSwitchDrivingSchoolIfNecessary<DrivingSchoolCustomCard>((it) => {
    const { drivingSchoolId } = it;
    if (!drivingSchoolId) {
      throw new Error(`CustomCard ${it.id} has no drivingSchoolId`);
    }
    return drivingSchoolId;
  });

  if (!grants.has("CustomCard:edit")) {
    return <AccessDenied />;
  }

  if (!customCard) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      resource="customCards"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Teaser erfolgreich aktualisiert.");
          redirect(`/drivingSchools/${customCard.drivingSchoolId}/customCards`);
        },
        onError: (error: unknown) => {
          showErrorToast("Fehler beim Aktualisieren des Teasers.");
          console.error("Failed to update drivingSchoolCustomCard.", error);
        },
      }}
      mutationMode="pessimistic"
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel="App Cockpit Teaser"
              backUrl={`/drivingSchools/${customCard.drivingSchoolId}/customCards`}
              title={customCard.title}
            />
          }
        />
      }
    >
      <DrivingSchoolCustomCardForm mode="edit" />
    </Edit>
  );
}

function DrivingSchoolCustomCardForm(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  return (
    <Form2 warnWhenUnsavedChanges>
      <Column sx={{ padding: overviewCardStyle.padding }}>
        <_CustomCardImageInput mode={mode} />
        <TextInput
          source="title"
          label="Titel"
          autoFocus={mode === "create"}
          validate={required()}
          sx={{ width: "40%" }}
        />
        <TextInput source="description" label="Text" validate={required()} sx={{ width: "40%" }} />
        <TextInput source="ctaLabel" label="Button Beschriftung" sx={{ width: "40%" }} />
        <TextInput source="ctaLink" label="Button Link" validate={url()} sx={{ width: "40%" }} />
      </Column>
      <_Toolbar />
    </Form2>
  );
}

function _CustomCardImageInput(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  const {
    field: { value: image },
  } = useInput({ source: "image" });
  const customCard = useRecordContext<DrivingSchoolCustomCard>();
  const hasSelectedImage = !!image;
  return (
    <Column sx={{ width: "40%" }}>
      {mode === "edit" && !hasSelectedImage && customCard?.imageUrl && (
        <Box sx={{ mb: "12px" }}>
          <Box component="p" sx={{ m: "0 0 8px", color: "text.secondary" }}>
            Aktuelles Bild
          </Box>
          <img src={customCard.imageUrl} alt="Aktuelles Bild" style={{ maxWidth: "100%", maxHeight: "180px" }} />
        </Box>
      )}
      <ImageInput
        source="image"
        label={mode === "edit" ? "Bild ersetzen" : "Bild"}
        validate={mode === "create" ? required() : undefined}
        accept="image/png,image/jpeg,.png,.jpg,.jpeg"
        options={{ multiple: false }}
        sx={_fileInputDropZoneStyle}
      >
        <ImageField source="src" />
      </ImageInput>
    </Column>
  );
}

function _Toolbar(props: ToolbarProps) {
  const { defaultTitle: creatingRecord } = useCreateContext();
  const [{ drivingSchoolId }] = useAutovioContext();
  return (
    <Toolbar {...props} sx={{ justifyContent: "space-between" }}>
      <SaveButton2 />
      {!creatingRecord && grants.has("CustomCard:delete") && (
        <DeleteRecordButton onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/customCards`} />
      )}
    </Toolbar>
  );
}

export const drivingSchoolCustomCardsResource = (
  <Resource
    key="customCards"
    name="customCards"
    list={DefaultList}
    create={CreateCustomCardForm}
    edit={EditCustomCardForm}
    options={{
      label: "App Cockpit Teaser",
      createTitle: "Neuer App Cockpit Teaser",
      noEntriesText: "Aktuell werden keine zusätzlichen Teaser im Cockpit der AUTOVIO App angezeigt.",
      noMatchingEntriesText: "Keine passenden Teaser gefunden.",
    }}
    recordRepresentation={(customCard: DrivingSchoolCustomCard) => customCard.title}
  />
);
